import { styled } from 'goober';

import { mediaQuery } from '@/theme/setupTheme';
import { ParagraphMedium, ParagraphSmall } from '@/ui/typography/widgets';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Icon } from '@/ui/icons/icon';
import { strokeColorIcon } from '@/ui/icons/functions';

import { SpotlightInfoTooltip } from '../spotlight-info-tooltip';
import { useSpotlightSummary } from './use-spotlight-summary';

export const SpotlightSummary = () => {
  const summary = useSpotlightSummary();

  return (
    <section data-testid="spotlight-summary-section">
      <Flexbox fullWidth gap="16px" wrap="wrap">
        {Object.values(summary).map(s => (
          <SummaryWidget
            key={s.description}
            data-testid="spotlight-summary-widget"
          >
            <TooltipAnchor>
              <SpotlightInfoTooltip
                dataTestId="spotlight-summary-tooltip"
                description={s.tooltip.description}
                title={s.tooltip.title}
              />
            </TooltipAnchor>
            <IconContainer
              justify="center"
              alignItems="center"
              backgroundColor={s.icon.background}
              color={s.icon.color}
            >
              <Icon icon={s.icon.variant} width="20px" height="20px" />
            </IconContainer>
            <SummaryContent direction="column" gap="4px">
              <Count>{s.value}</Count>
              <Description>{s.description}</Description>
            </SummaryContent>
          </SummaryWidget>
        ))}
      </Flexbox>
    </section>
  );
};

const SummaryWidget = styled(Flexbox)`
  align-items: flex-start;
  padding: 16px;
  min-height: 95px;
  flex: 1 1 200px;
  gap: 12px;
  min-width: 200px;
  position: relative;
  background-color: ${({ theme }) => theme.colors.basics.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadow.spotlight.summary};

  ${mediaQuery('lg', 'min')} {
    flex: 1 1 0;
    min-width: 0;
  }
`;

const TooltipAnchor = styled('div')`
  position: absolute;
  right: 12px;
  top: 12px;
`;

const SummaryContent = styled(Flexbox)`
  flex: 1;
  min-width: 0;
`;

const IconContainer = styled(Flexbox)<{
  backgroundColor: string;
  color: string;
}>`
  border-radius: 5px;
  flex-grow: 0;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background-color: ${({ backgroundColor }) => backgroundColor};
  ${({ color }) => strokeColorIcon(color)}
`;

const Count = styled(ParagraphMedium)`
  ${({ theme }) => theme.typography.spotlight.summary.count}
  margin: 0;
`;

const Description = styled(ParagraphSmall)`
  ${({ theme }) => theme.typography.spotlight.summary.description}
  margin: 0;
  overflow-wrap: break-word;
`;
